/**
 * player-api.ts - API client for player operations
 *
 * Handles all HTTP requests related to player management and search.
 */

import type {
  Player,
  AddPlayerRequest,
  UpdatePlayerRequest,
  PlayerSearchResult,
  ApiError
} from '../../types/api';
import { API_BASE_URL } from '../utils/constants';

/**
 * Add a player to a tournament
 * @param tournamentId - Tournament ID
 * @param playerData - Player data
 * @returns Created player object
 * @throws Error if request fails
 */
export async function addPlayerAPI(
  tournamentId: string,
  playerData: AddPlayerRequest
): Promise<Player> {
  const response = await fetch(`${API_BASE_URL}/${tournamentId}/players`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(playerData)
  });

  if (!response.ok) {
    const error: ApiError = await response.json();
    throw new Error(error.error || 'Failed to add player');
  }

  return await response.json();
}

/**
 * Update a player
 * @param tournamentId - Tournament ID
 * @param playerId - Player ID
 * @param playerData - Updated player data
 * @returns Updated player object
 * @throws Error if request fails
 */
export async function updatePlayerAPI(
  tournamentId: string,
  playerId: string,
  playerData: UpdatePlayerRequest
): Promise<Player> {
  const response = await fetch(`${API_BASE_URL}/${tournamentId}/players/${playerId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(playerData)
  });

  if (!response.ok) {
    const error: ApiError = await response.json();
    throw new Error(error.error || 'Failed to update player');
  }

  return await response.json();
}

/**
 * Remove a player from a tournament
 * @param tournamentId - Tournament ID
 * @param playerId - Player ID
 * @throws Error if request fails
 */
export async function removePlayerAPI(
  tournamentId: string,
  playerId: string
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/${tournamentId}/players/${playerId}`, {
    method: 'DELETE',
    credentials: 'include'
  });

  if (!response.ok) {
    const error: ApiError = await response.json();
    throw new Error(error.error || 'Failed to remove player');
  }
}

/**
 * Search FIDE database for players
 * @param searchTerm - Search term
 * @returns Array of player search results
 * @throws Error if request fails
 */
export async function searchFidePlayer(searchTerm: string): Promise<PlayerSearchResult[]> {
  const response = await fetch(
    `${API_BASE_URL}/fide-search?name=${encodeURIComponent(searchTerm)}`,
    { credentials: 'include' }
  );

  if (!response.ok) {
    throw new Error('Failed to search FIDE database');
  }

  return await response.json();
}

/**
 * Search SwissChess database for players
 * @param searchTerm - Search term
 * @returns Array of player search results
 * @throws Error if request fails
 */
export async function searchSwissChessPlayer(searchTerm: string): Promise<PlayerSearchResult[]> {
  const response = await fetch(
    `${API_BASE_URL}/swisschess-search?name=${encodeURIComponent(searchTerm)}`,
    { credentials: 'include' }
  );

  if (!response.ok) {
    throw new Error('Failed to search SwissChess database');
  }

  return await response.json();
}
